import { useEffect, useCallback } from 'react';
import useTranslation from 'next-translate/useTranslation';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import { useShowSplashNotification } from '~/domains/common/hooks/useShowSplashNotification';
import type { AuthSuccessResponse } from '~/services/identity/unauthenticated/identity.service.api';
import dt from '~/testing';

import useEmailVerification from '../../hooks/useEmailVerification';
import { EmailVerificationForm } from '../SignUpSteps/components/EmailVerificationForm';

const dtIds = dt.components.shared.auth.signUpVersionTwo.emailVerificationForm;

type EmailVerificationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  verificationToken: string;
  email: string;
  canResendIn?: number;
  dismissable?: boolean;
  onVerifySuccess: (data: AuthSuccessResponse) => void | Promise<void>;
  onTooManyAttempts?: () => void;
  onStartOver?: () => void;
  onContactSupport?: () => void;
};

export type { EmailVerificationModalProps };

export default function ConnectedEmailVerificationModal({
  isOpen,
  onClose,
  verificationToken,
  email,
  canResendIn: initialCanResendIn,
  dismissable,
  onVerifySuccess,
  onTooManyAttempts,
  onStartOver,
  onContactSupport,
}: EmailVerificationModalProps) {
  const { t } = useTranslation('auth');
  const { showSplashNotification } = useShowSplashNotification();

  const {
    verificationStatus,
    verificationErrorMessage,
    canResendIn,
    code,
    handleCodeChange,
    isTooManyAttempts,
    handleResend,
    initMfa,
  } = useEmailVerification({
    onVerifySuccess: async (data) => {
      await onVerifySuccess(data);
      onClose();
    },
    onResendError: (beMessage?: string) => {
      showSplashNotification({
        title: t('signUp.errors.resendFailed.title'),
        message: beMessage ?? t('signUp.errors.resendFailed.message'),
        color: 'red',
        autoClose: 5000,
      });
    },
  });

  useEffect(() => {
    if (verificationToken && email) {
      initMfa(verificationToken, email, initialCanResendIn);
    }
  }, [verificationToken, email, initialCanResendIn, initMfa]);

  useEffect(() => {
    if (isTooManyAttempts) {
      onClose();
      onTooManyAttempts?.();
    }
  }, [isTooManyAttempts, onClose, onTooManyAttempts]);

  const handleStartOver = useCallback(() => {
    onClose();
    onStartOver?.();
  }, [onClose, onStartOver]);

  return (
    <Dialog
      type={{ desktop: 'modal', mobile: 'drawer' }}
      isOpen={isOpen}
      onClose={dismissable ? onClose : undefined}
      dataTestId={dtIds.modal}
    >
      <EmailVerificationForm
        email={email}
        code={code}
        onCodeChange={handleCodeChange}
        onResendCode={handleResend}
        onStartOver={onStartOver ? handleStartOver : undefined}
        onContactSupport={onContactSupport}
        verificationStatus={verificationStatus}
        resendCooldownSeconds={canResendIn}
        errorMessage={verificationErrorMessage}
      />
    </Dialog>
  );
}
